import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Tabs,
  Tab,
  LinearProgress,
  Alert as MuiAlert,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormHelperText,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BoltIcon from "@mui/icons-material/Bolt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { QUESTIONNAIRE_CONFIGS, getBranchCode } from "../lib/questionnaire-config";
import {
  getVisibleQuestions,
  calculateCompletionScore,
  getQualityRating,
  generateAlerts,
} from "../lib/questionnaire-logic";

// ─── Costanti UI ─────────────────────────────────────────────────────────────

const QUALITY_COLOR = {
  underwriting_ready: "success",
  completo: "success",
  buono: "warning",
  incompleto: "error",
};

const QUALITY_LABEL = {
  underwriting_ready: "Pronto per underwriting",
  completo: "Completo",
  buono: "Buon livello",
  incompleto: "Incompleto",
};

const HISTORY_TOGGLE_PATTERN = /(sinistri|incidenti|contenziosi)/i;

// ─── Renderer domande ────────────────────────────────────────────────────────

function QuestionLabel({ question }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {question.text}
      {question.required && (
        <Typography component="span" color="error" sx={{ lineHeight: 1 }}>
          *
        </Typography>
      )}
      {(question.tooltip || question.helpText) && (
        <Tooltip title={question.tooltip || question.helpText} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      )}
    </Box>
  );
}

function isAnswerProvided(question, value) {
  if (question.type === "multiselect") {
    return Array.isArray(value) && value.length > 0;
  }

  if (question.type === "claims_table") {
    return Array.isArray(value) && value.length > 0;
  }

  if (question.type === "toggle") {
    return value === true || value === false;
  }

  return value !== undefined && value !== null && value !== "";
}

function renderQuestion(question, value, onChange, hasError) {
  switch (question.type) {
    case "toggle":
      return (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                color="primary"
              />
            }
            label={<QuestionLabel question={question} />}
          />
          {hasError && (
            <FormHelperText error>Campo obbligatorio</FormHelperText>
          )}
        </Box>
      );

    case "text":
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          size="small"
          error={hasError}
          helperText={hasError ? "Campo obbligatorio" : ""}
        />
      );

    case "number":
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          type="number"
          fullWidth
          size="small"
          error={hasError}
          helperText={hasError ? "Campo obbligatorio" : ""}
          inputProps={{ min: 0 }}
        />
      );

    case "textarea":
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          error={hasError}
          helperText={hasError ? "Campo obbligatorio" : ""}
        />
      );

    case "currency":
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          type="number"
          fullWidth
          size="small"
          error={hasError}
          helperText={hasError ? "Campo obbligatorio" : ""}
          InputProps={{
            startAdornment: <InputAdornment position="start">€</InputAdornment>,
          }}
          inputProps={{ min: 0 }}
        />
      );

    case "date":
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          type="date"
          fullWidth
          size="small"
          error={hasError}
          helperText={hasError ? "Campo obbligatorio" : ""}
          InputLabelProps={{ shrink: true }}
        />
      );

    case "select":
      return (
        <FormControl fullWidth size="small" error={hasError}>
          <InputLabel>
            <QuestionLabel question={question} />
          </InputLabel>
          <Select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            label={question.text}
          >
            <MenuItem value="">
              <em>— Seleziona —</em>
            </MenuItem>
            {(question.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {hasError && <FormHelperText>Campo obbligatorio</FormHelperText>}
        </FormControl>
      );

    case "multiselect":
      if (question.id === "tl_ambito" || question.id === "inf_categorie") {
        return (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: "block", mb: 1 }}
            >
              <QuestionLabel question={question} />
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {(question.options || []).map((opt) => {
                const isSelected = (value || []).includes(opt);

                return (
                  <Chip
                    key={opt}
                    label={opt}
                    clickable
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => {
                      const current = value || [];
                      onChange(
                        isSelected
                          ? current.filter((item) => item !== opt)
                          : [...current, opt]
                      );
                    }}
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Stack>
            {hasError && (
              <FormHelperText error>Seleziona almeno un'opzione</FormHelperText>
            )}
          </Box>
        );
      }

      return (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            <QuestionLabel question={question} />
          </Typography>
          <FormGroup row sx={{ gap: 0 }}>
            {(question.options || []).map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    checked={(value || []).includes(opt)}
                    onChange={(e) => {
                      const current = value || [];
                      onChange(
                        e.target.checked
                          ? [...current, opt]
                          : current.filter((o) => o !== opt)
                      );
                    }}
                    size="small"
                  />
                }
                label={opt}
                sx={{ mr: 2 }}
              />
            ))}
          </FormGroup>
          {hasError && (
            <FormHelperText error>Seleziona almeno un'opzione</FormHelperText>
          )}
        </Box>
      );

    case "claims_table": {
      const rows = value || [];
      return (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 1 }}
          >
            <QuestionLabel question={question} />
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Anno</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descrizione</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Importo</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField
                        value={row.anno || ""}
                        onChange={(e) => {
                          const updated = [...rows];
                          updated[i] = { ...updated[i], anno: e.target.value };
                          onChange(updated);
                        }}
                        type="number"
                        size="small"
                        sx={{ width: 85 }}
                        inputProps={{ min: 2000, max: new Date().getFullYear() }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.descrizione || ""}
                        onChange={(e) => {
                          const updated = [...rows];
                          updated[i] = { ...updated[i], descrizione: e.target.value };
                          onChange(updated);
                        }}
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.importo || ""}
                        onChange={(e) => {
                          const updated = [...rows];
                          updated[i] = { ...updated[i], importo: e.target.value };
                          onChange(updated);
                        }}
                        type="number"
                        size="small"
                        sx={{ width: 130 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">€</InputAdornment>
                          ),
                        }}
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.disabled" }}>
                      Nessun sinistro aggiunto
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              onChange([
                ...rows,
                { anno: new Date().getFullYear(), descrizione: "", importo: "" },
              ])
            }
            sx={{ mt: 1 }}
          >
            Aggiungi sinistro
          </Button>
          {hasError && (
            <FormHelperText error>Aggiungi almeno un sinistro</FormHelperText>
          )}
        </Box>
      );
    }

    default:
      return (
        <TextField
          label={<QuestionLabel question={question} />}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          size="small"
        />
      );
  }
}

// ─── Componente principale ────────────────────────────────────────────────────

export default function QuestionnaireDialog({
  open,
  onClose,
  onSubmit,
  copertura,
  knownData,
}) {
  const branchCode = getBranchCode(copertura?.tipo);
  const config = branchCode ? QUESTIONNAIRE_CONFIGS[branchCode] : null;

  const [mode, setMode] = useState("quick");
  const [activeStep, setActiveStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Reset completo all'apertura del dialog
  useEffect(() => {
    if (open) {
      setMode("quick");
      setActiveStep(0);
      setResponses({});
      setShowValidationErrors(false);
    }
  }, [open]);

  const steps = config?.[mode] || [];
  const currentStep = steps[activeStep] || { title: "", questions: [] };
  const visibleQuestions = getVisibleQuestions(currentStep.questions || [], responses);

  const completionScore = config
    ? calculateCompletionScore(config, responses, mode)
    : 0;
  const qualityRating = getQualityRating(completionScore);
  const alerts = generateAlerts({}, responses, mode, knownData);

  const missingRequiredQuestions = visibleQuestions.filter(
    (q) => q.required && !isAnswerProvided(q, responses[q.id])
  );
  const hasMissingRequired = missingRequiredQuestions.length > 0;

  const handleResponse = (id, value) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
  };

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    setMode(newMode);
    setActiveStep(0);
    setResponses({});
    setShowValidationErrors(false);
  };

  const handleNextStep = () => {
    if (hasMissingRequired) {
      setShowValidationErrors(true);
      return;
    }
    setShowValidationErrors(false);
    setActiveStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (hasMissingRequired) {
      setShowValidationErrors(true);
      return;
    }

    const normalizedResponses = { ...responses };
    steps.forEach((step) => {
      step.questions.forEach((question) => {
        if (
          question.type === "toggle" &&
          HISTORY_TOGGLE_PATTERN.test(question.id) &&
          normalizedResponses[question.id] === undefined
        ) {
          normalizedResponses[question.id] = false;
        }
      });
    });

    setSubmitting(true);
    try {
      await onSubmit?.({
        copertura,
        mode,
        branchCode,
        responses: normalizedResponses,
        completionScore,
        qualityRating,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  // ── Fallback: nessuna config trovata ────────────────────────────────────
  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Questionario Preventivo</DialogTitle>
        <DialogContent>
          <MuiAlert severity="info" sx={{ mt: 1 }}>
            Nessun questionario configurato per:{" "}
            <strong>{copertura?.tipo || "questa copertura"}</strong>
          </MuiAlert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* ── Titolo ───────────────────────────────────────────────────────── */}
      <DialogTitle sx={{ pb: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6" fontWeight={700}>
            {config.label || copertura?.tipo}
          </Typography>
          <Chip
            label={`${completionScore}% — ${QUALITY_LABEL[qualityRating]}`}
            color={QUALITY_COLOR[qualityRating]}
            size="small"
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {/* ── Selezione modalità ──────────────────────────────────────────── */}
        <Tabs
          value={mode}
          onChange={handleModeChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            value="quick"
            label="Raccolta Rapida"
            icon={<BoltIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            value="full"
            label="Underwriting Completo"
            icon={<AccountTreeIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>

        {/* ── Barra completamento ─────────────────────────────────────────── */}
        <LinearProgress
          variant="determinate"
          value={completionScore}
          sx={{ mb: 2.5, height: 6, borderRadius: 3 }}
          color={QUALITY_COLOR[qualityRating] === "error" ? "error" : QUALITY_COLOR[qualityRating] === "warning" ? "warning" : "success"}
        />

        {/* ── Stepper (solo full) ─────────────────────────────────────────── */}
        {mode === "full" && steps.length > 1 && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((s) => (
              <Step key={s.title}>
                <StepLabel>{s.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* ── Alert assuntivi dinamici ────────────────────────────────────── */}
        {alerts.length > 0 && (
          <Stack spacing={1} sx={{ mb: 2.5 }}>
            {alerts.map((alert, i) => (
              <MuiAlert
                key={i}
                severity={
                  alert.type === "critical"
                    ? "error"
                    : alert.type === "info"
                    ? "info"
                    : "warning"
                }
                variant="outlined"
              >
                {alert.message}
              </MuiAlert>
            ))}
          </Stack>
        )}

        {showValidationErrors && hasMissingRequired && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            Compila tutti i campi obbligatori per proseguire.
          </MuiAlert>
        )}

        {/* ── Titolo step ─────────────────────────────────────────────────── */}
        {currentStep.title && (
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
            {currentStep.title}
          </Typography>
        )}

        {/* ── Domande ─────────────────────────────────────────────────────── */}
        <Stack spacing={2.5}>
          {visibleQuestions.map((q) => (
            <Box key={q.id}>
              {renderQuestion(
                q,
                responses[q.id],
                (val) =>
                handleResponse(q.id, val)
                ,
                showValidationErrors && q.required && !isAnswerProvided(q, responses[q.id])
              )}
              {/* Alert inline sulla domanda */}
              {q.alert && responses[q.id] !== undefined && (
                <MuiAlert
                  severity={q.alert.type === "critical" ? "error" : "warning"}
                  variant="outlined"
                  sx={{ mt: 0.75 }}
                >
                  {q.alert.message}
                </MuiAlert>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>

      {/* ── Azioni ──────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Annulla
        </Button>
        {mode === "full" && activeStep > 0 && (
          <Button onClick={() => setActiveStep((s) => s - 1)} variant="outlined">
            Indietro
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {mode === "full" && !isLastStep ? (
          <Button onClick={handleNextStep} variant="contained">
            Avanti
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || hasMissingRequired}
          >
            Completa Questionario
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
